import React from "react";
import {
  type ProcessedRaceMeeting,
  useGroupedRaceMeetings,
} from "@/hooks/firestore/v2/betting/useRaceMeetings";
import { useSearchParams } from "@/hooks/useSearchParams";
import { racingFilters } from "@/features/racing/models/racingFilters";
import { MeetingGroup } from "@/features/racing/components/MeetingGroup";
import useRestoreScrollPosition from "@/hooks/useRestoreScrollPosition";
import { RacesByDaySkeleton } from "@/features/racing/components/RacesByDaySkeleton";
import { ButtonGroup } from "@/components/ButtonGroup";
import { Button } from "@/components/Button";
import { PageMessage } from "@/components/PageMessage";
import { useIsMobile } from "@/hooks/useDisplay";
import { navigate } from "@/utilities/navigate";
import { ReactComponent as OddsUnavailable } from "components/assets/icon-empty-odds.svg";
import { useTitleOverride } from "@/features/racing/hooks/useTitleOverride";
import { useLocation } from "@/hooks/useLocation";

import * as styles from "./RacesByDay.module.scss";

type RaceByDayProps = {
  startDate: Date;
  endDate: Date;
};

const RacesByDay = ({ startDate, endDate }: RaceByDayProps) => {
  const {
    searchParams: { regions, titles },
  } = useSearchParams(racingFilters);
  const { search } = useLocation();

  const forcedTitles = useTitleOverride();
  const isMobile = useIsMobile();
  const { meetings, isLoading } = useGroupedRaceMeetings({
    startDate,
    endDate,
    regions,
    titles: forcedTitles.length > 0 ? forcedTitles : titles,
  });

  useRestoreScrollPosition(isLoading);

  if (isLoading) {
    return <RacesByDaySkeleton />;
  }

  if (!meetings)
    return (
      <PageMessage
        title="No Races Found"
        image={<OddsUnavailable />}
        subTitle="There are currently no races which match your filters"
      >
        <ButtonGroup>
          <Button
            variant={`primary`}
            size={isMobile ? `md` : `default`}
            onClick={() => navigate(`/racing/betting/next-to-jump/${search}`)}
          >
            Next to Jump
          </Button>
        </ButtonGroup>
      </PageMessage>
    );

  return (
    <div className={styles.container}>
      {(Object.keys(meetings) as ProcessedRaceMeeting["sport"][]).map(
        (title) => {
          const meetingsByTitle = meetings[title];

          if (!meetingsByTitle) return;

          return (
            <MeetingGroup
              key={`meetings-${title}`}
              title={title}
              meetings={meetingsByTitle}
            />
          );
        },
      )}
    </div>
  );
};

export default RacesByDay;
